<script setup lang="ts">
import appCan from '@@/bits/app_can'
import { dir } from '@@/bits/current_dir'
import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { defineAsyncComponent } from '@@/bits/vue'
import { useAccessibilitySettingsStore } from '@@/pinia/accessibility_settings_store'
import { useAppBarAccountStore } from '@@/pinia/app_bar_account'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useLibraryPlansStore } from '@@/pinia/library_plans'
import { PadletSelectionIntent } from '@@/pinia/padlet_picker'
import { usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceCommentDrawer } from '@@/vuecomposables/useSurfaceCommentDrawer'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceExpandedCommentStore } from '@@/pinia/surface_expanded_comment'
import { useSurfaceFreezePadletPanel } from '@@/pinia/surface_freeze_padlet_panel'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfacePostGroupByStore } from '@@/pinia/surface_post_group_by_store'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceRequireNoticeStore } from '@@/pinia/surface_require_notice_store'
import { useSurfaceScreenRecorderStore } from '@@/pinia/surface_screen_recorder_store'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Id } from '@@/types'
import usePostDuplicateAndTransfer from '@@/vuecomposables/post_duplicate_and_transfer'
import { AVATAR_EL_ID, useSurfaceNavbarVisibility } from '@@/vuecomposables/surface_navbar_visibility'
import { installTranslations } from '@padlet/universal-post-editor'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'

// IMPORTANT: If you're adding a new panel here, please also add it to the
// `isOverlayVisible` getter in `app/javascript/vuexstore/surface/index.ts`
const SurfaceContributors = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContributors.vue'))
const SurfaceSearchBar = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSearchBar.vue'))
const SurfaceDetailsPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceDetailsPanel.vue'))
const SurfaceSettingsPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSettingsPanel.vue'))
const SurfaceSettingsVideoRecorder = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSettingsVideoRecorder.vue'),
)
const SurfaceSettingsScreenRecorder = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSettingsScreenRecorder.vue'),
)
const SurfaceSettingsPostPropertiesCustomFieldPanel = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSettingsPostPropertiesCustomFieldPanel.vue'),
)
const SurfaceSharePanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSharePanel.vue'))
const SurfaceRemakePanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceRemakePanel.vue'))
const SurfacePanelPostExpanded = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePanelPostExpanded.vue'))
const SurfacePanelPostActionMenuNew = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePanelPostActionMenu.vue'),
)
const SurfacePhoneNumberFieldMethodMenu = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePhoneNumberFieldMethodMenu.vue'),
)
const SurfaceSectionActionMenu = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSectionActionMenu.vue'))
const SurfaceCommentActionMenu = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceCommentActionMenu.vue'))
const SurfaceComposerModalList = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceComposerModalList.vue'))
const UpgradeRequired = defineAsyncComponent(() => import('@@/vuecomponents/upgrade_required.vue'))
const SurfaceDropdownReactionStar = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceDropdownReactionStar.vue'),
)
const SurfaceDropdownReactionGrade = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceDropdownReactionGrade.vue'),
)
const DialogPermitNotification = defineAsyncComponent(() => import('@@/vuecomponents/DialogPermitNotification.vue'))
const SurfaceDialogExtensionRequired = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceDialogExtensionRequired.vue'),
)
const SurfaceSectionRenameModal = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSectionRenameModal.vue'))
const SurfaceCommentDrawerAddComment = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceCommentDrawerAddComment.vue'),
)
const SurfaceCommentDrawerEditComment = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceCommentDrawerEditComment.vue'),
)
const MembershipTierUpgradeModal = defineAsyncComponent(() => import('@@/vuecomponents/MembershipTierUpgradeModal.vue'))
const SurfaceEditBookmarkModal = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceEditBookmarkModal.vue'))
const MembershipLibraryTierUpgradeModal = defineAsyncComponent(
  () => import('@@/vuecomponents/MembershipLibraryTierUpgradeModal.vue'),
)
const UserMenuPopoverModal = defineAsyncComponent(() => import('@@/vuecomponents/UserMenuPopoverModal.vue'))
const SurfaceGuestIdCard = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceGuestIdCard.vue'))
const SurfaceActivityPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceActivityPanel.vue'))
const SurfaceReportPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceReportPanel.vue'))
const SurfaceDevelopersPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceDevelopersPanel.vue'))
const SurfaceFreezePadletPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceFreezePadletPanel.vue'))
const SurfaceQrCodeDialog = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceQrCodeDialog.vue'))
const SurfaceAutomationsPanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAutomationsPanel.vue'))
const SurfacePadletPickerV2 = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePadletPickerV2.vue'))
const SurfacePanelCommentImageExpanded = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePanelCommentImageExpanded.vue'),
)
const SurfaceLanguagePanel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceLanguagePanel.vue'))
const AccessibilitySettingsModal = defineAsyncComponent(() => import('@@/vuecomponents/AccessibilitySettingsModal.vue'))
const SurfaceScreenRecorder = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceScreenRecorder.vue'))
const SurfacePhotoAlbumContentPicker = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePhotoAlbumContentPicker.vue'),
)
const SurfacePhotoAlbumEditorDialog = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePhotoAlbumEditorDialog.vue'),
)
const SurfaceTranslationDrawer = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceTranslationDrawer.vue'))
const SurfaceEmojiPicker = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceEmojiPicker.vue'))
const SurfaceAdditionalOptionsPanel = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceAdditionalOptionsPanel.vue'),
)

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const { xMobileTranslationBanner } = storeToRefs(useSurfaceTranslationsStore())

const { xAddCommentDrawer, xEditCommentDrawer } = useSurfaceCommentDrawer()
const { xGuestIdCard } = useSurfaceNavbarVisibility()

const userMenuAnchor = computed(() => {
  if (!xUserMenu.value || isMobileLayout.value) return null
  return document.getElementById(AVATAR_EL_ID)
})

const guestIdCardAnchor = computed(() => {
  if (!xGuestIdCard.value) return null
  return document.getElementById(AVATAR_EL_ID)
})

const isApp = device.app
const isNativeMenu = appCan('showAlertSheet')

const surfaceStore = useSurfaceStore()
const {
  xWallQuotaExceeded,
  fileThatExceededSize,
  xPermitNotification,
  isFirstView,
  xSearchBar,
  user,
  isMobileLayout,
  xNewComposerModalPanel,
  canUseSections,
  xSettingsPanel,
  hasSidePanelOutsideSurface,
  backgroundLuminance,
  buttonDarkMode,
} = storeToRefs(surfaceStore)
const { xContributorsList } = storeToRefs(useSurfaceUserContributorsStore())
const { postUnderActionCid, xTransferPostPanel, postBeingTransferredCid, xCopyPostPanel, postBeingCopiedCid } =
  storeToRefs(useSurfacePostActionStore())
const { xExpandedPostPanel } = storeToRefs(useExpandedPostStore())
const { xUserMenu } = storeToRefs(useAppBarAccountStore())
const { xUpgradeModal: xLibraryUpgradeModal } = storeToRefs(useLibraryPlansStore())
const { xUpgradeModal } = storeToRefs(usePersonalPlansStore())
const commentsStore = useCommentsStore()
const { commentUnderActionId } = storeToRefs(commentsStore)
const { sectionUnderActionId, xSectionRenameModal } = storeToRefs(useSurfaceSectionsStore())
const { groupUnderActionId, xGroupRenameModal } = storeToRefs(useSurfacePostGroupByStore())
const { isSmallerThanTabletPortrait } = storeToRefs(useWindowSizeStore())
const { isContainerSmallerThanTabletLandscape } = storeToRefs(useSurfaceContainerSizeStore())
const { xSurfaceFreezePanel } = storeToRefs(useSurfaceFreezePadletPanel())
const {
  xSurfaceDetailsPanel,
  xPhoneFieldMethodPopover,
  xQrModal,
  xSurfaceActivityPanel,
  xSurfaceSharePanel,
  xSurfaceRemakePanel,
  xSurfaceAutomationsPanel,
  xPhotoAlbumImagePicker,
  xPhotoAlbumEditorDialog,
  xDevelopersPanel,
  xReportPanel,
  xEditBookmarksDialog,
  xEmojiPicker,
  xActionBarMoreMenu,
} = storeToRefs(useSurfacePanelsStore())
const { originalReactionData, postIdBeingReactedTo } = storeToRefs(useReactionsStore())
const { xExpandedCommentImagePanel } = storeToRefs(useSurfaceExpandedCommentStore())

const { demoPadletPanelVisibility, isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())
const { xSurfaceLanguagePanel } = storeToRefs(useSurfaceTranslationsStore())

const accessibilitySettingsStore = useAccessibilitySettingsStore()
const surfaceScreenRecorderStore = useSurfaceScreenRecorderStore()

const xCurtain = computed(() => {
  if (hasSidePanelOutsideSurface.value && isSmallerThanTabletPortrait.value) return false
  return xSurfaceSharePanel.value || xCopyPostPanel.value || xTransferPostPanel.value
})

const xCommentMenu = computed(() => {
  return commentUnderActionId.value && !isNativeMenu
})

const hasInstalledTranslations = ref(false)

onMounted(() => {
  // Need to install translations for the post editor before it's rendered.
  // We do it here so it's installed the earliest possible.
  installTranslations(getHyphenatedCurrentLocale()).then(() => {
    hasInstalledTranslations.value = true
  })

  if (isAppUsing('onboardingPanel')) {
    return
  }
  if (isFirstView.value) {
    showSettingsPanel()
  }
})

const surfaceRequireNoticeStore = useSurfaceRequireNoticeStore()
const { xExtensionRequired } = storeToRefs(surfaceRequireNoticeStore)
const { hideExtensionRequired } = surfaceRequireNoticeStore
const { hideWallQuotaExceeded, hidePermitNotification } = surfaceStore
const { endPostTransfer, endPostCopy } = useSurfacePostActionStore()
const { closeUpgradeModal: closeLibraryUpgradeModal } = useLibraryPlansStore()
const { showSettingsPanel } = useSurfaceSettingsStore()
const { closeExpandedCommentImagePanel } = useSurfaceExpandedCommentStore()

const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
const { xVideoRecorderSettingsPanel, xScreenRecorderSettingsPanel, xCustomPostPropertiesPanel } =
  storeToRefs(surfacePostPropertiesStore)
const { hideVideoRecorderSettingsPanel, hideScreenRecorderSettingsPanel } = surfacePostPropertiesStore

function closePlanUpgradeModal() {
  usePersonalPlansStore().closePlanUpgradeModal()
}

const { duplicatePost, transferPost, isCopyingPost, isTransferringPost } = usePostDuplicateAndTransfer()
</script>

<template>
  <div id="surface-panels" class="surface-panels">
    <div v-if="xCurtain" class="curtain is-animated fade-in" />
    <UserMenuPopoverModal
      v-if="xUserMenu"
      :user="user"
      v-bind="
        isAppUsing('surfaceTopbar') && userMenuAnchor
          ? {
              anchor: userMenuAnchor,
              darkMode: buttonDarkMode,
            }
          : {
              anchorLeftOffset: isMobileLayout ? 22 : 16,
              anchorTopOffset: isMobileLayout ? 4 : 16,
              isUsedOnSurface: true,
            }
      "
    />
    <SurfaceGuestIdCard v-if="xGuestIdCard && guestIdCardAnchor" :anchor="guestIdCardAnchor" />
    <SurfaceContributors v-if="xContributorsList" />
    <SurfaceSearchBar
      v-if="
        !isApp && isContainerSmallerThanTabletLandscape && xSearchBar && demoPadletPanelVisibility.showSurfaceSearchBar
      "
      :class="[
        'fixed start-0 end-0',
        hasSidePanelOutsideSurface && isSmallerThanTabletPortrait ? '-bottom-1' : 'bottom-0',
      ]"
      :should-animate-on-appear="true"
    />
    <SurfaceSettingsPanel v-if="xSettingsPanel" />
    <SurfaceSharePanel v-if="xSurfaceSharePanel" />

    <!-- Place this after the settings and share panels, as it needs to be rendered over them. -->
    <SurfaceSettingsPostPropertiesCustomFieldPanel
      v-if="xCustomPostPropertiesPanel"
      :x-scrim="false /* never show scrim as this panel is always on top of other panels */"
    />

    <SurfaceQrCodeDialog v-if="xQrModal" @close="xQrModal = false" />
    <SurfaceRemakePanel v-if="xSurfaceRemakePanel" />
    <SurfacePanelPostExpanded
      v-if="xExpandedPostPanel && !isDemoPadletPanelDesktop"
      :reaction-ids-by-post-id="reactionIdsByPostId"
      :comment-ids-by-post-id="commentIdsByPostId"
    />
    <SurfacePanelCommentImageExpanded v-if="xExpandedCommentImagePanel" @close="closeExpandedCommentImagePanel" />
    <SurfacePanelPostActionMenuNew
      v-if="postUnderActionCid && !isNativeMenu && !isDemoPadletPanelDesktop"
      :post-cid="postUnderActionCid"
    />
    <SurfaceCommentActionMenu
      v-if="xCommentMenu && commentUnderActionId != null"
      :comment-id="Number(commentUnderActionId)"
      :usage-context="xExpandedPostPanel ? 'postExpandedView' : 'postView'"
    />
    <SurfacePadletPickerV2
      v-if="isAppUsing('padletPickerV2') && isTransferringPost && postBeingTransferredCid"
      :padlet-selection-intent="PadletSelectionIntent.TransferPost"
      :post-cid="postBeingTransferredCid"
      @select="transferPost"
      @close="endPostTransfer"
    />
    <SurfacePadletPickerV2
      v-if="isAppUsing('padletPickerV2') && isCopyingPost && postBeingCopiedCid"
      :padlet-selection-intent="PadletSelectionIntent.CopyPost"
      :post-cid="postBeingCopiedCid"
      @select="duplicatePost"
      @close="endPostCopy"
    />
    <SurfaceComposerModalList v-if="xNewComposerModalPanel && hasInstalledTranslations" />
    <!-- Aug 2021: replaced upgrade-required with `MembershipTierUpgradeModal` however not removed because potentially still used by very old app versions. -->
    <upgrade-required v-if="xWallQuotaExceeded" reason="walls" @close="hideWallQuotaExceeded" />

    <SurfaceCommentDrawerAddComment v-if="xAddCommentDrawer" />
    <SurfaceCommentDrawerEditComment v-if="xEditCommentDrawer" />

    <SurfaceEmojiPicker v-if="xEmojiPicker" />

    <!-- Any content pickers in surface should be rendered inside this div -->
    <!-- NOTE: Place modals that should appear beneath the content picker above this div. Place modals that should appear over the content picker below this div. -->
    <!-- Explanation: Since all modals have the same z-index, we control what gets rendered above the content picker and what gets rendered below the content picker by their relative order in the DOM -->
    <div id="content-picker-portal-target"></div>

    <SurfaceSettingsVideoRecorder
      v-if="xVideoRecorderSettingsPanel"
      :x-scrim="false"
      @close="hideVideoRecorderSettingsPanel"
    />

    <SurfaceSettingsScreenRecorder
      v-if="xScreenRecorderSettingsPanel"
      :x-scrim="false"
      @close="hideScreenRecorderSettingsPanel"
    />

    <MembershipTierUpgradeModal
      v-if="xUpgradeModal"
      :user="user"
      :file-that-exceeded-size="fileThatExceededSize ?? undefined"
      @cancel="closePlanUpgradeModal"
    />
    <MembershipLibraryTierUpgradeModal
      v-if="xLibraryUpgradeModal"
      :file-that-exceeded-size="fileThatExceededSize"
      @cancel="closeLibraryUpgradeModal"
    />
    <SurfaceDropdownReactionGrade
      v-if="postIdBeingReactedTo && originalReactionData.type == 'grade'"
      :dir="dir()"
      :usage-context="xExpandedPostPanel ? 'postExpandedView' : 'postView'"
    />
    <SurfaceDropdownReactionStar
      v-if="postIdBeingReactedTo && originalReactionData.type == 'star'"
      :dir="dir()"
      :usage-context="xExpandedPostPanel ? 'postExpandedView' : 'postView'"
    />

    <SurfaceDialogExtensionRequired v-if="xExtensionRequired" @close="hideExtensionRequired" />
    <DialogPermitNotification
      v-if="xPermitNotification && demoPadletPanelVisibility.showDialogPermitNotification"
      @close="hidePermitNotification"
    />
    <SurfaceEditBookmarkModal v-if="xEditBookmarksDialog" />
    <SurfaceSectionRenameModal
      v-if="(isAppUsing('groupPostsV2') ? xGroupRenameModal : xSectionRenameModal) && canUseSections"
    />
    <SurfaceActivityPanel v-if="xSurfaceActivityPanel" />
    <SurfaceSectionActionMenu v-if="isAppUsing('groupPostsV2') ? groupUnderActionId : sectionUnderActionId" />
    <SurfaceReportPanel v-if="xReportPanel" />
    <SurfaceDevelopersPanel v-if="xDevelopersPanel" />
    <SurfaceFreezePadletPanel v-if="xSurfaceFreezePanel" />
    <SurfaceDetailsPanel v-if="xSurfaceDetailsPanel" />
    <SurfaceAutomationsPanel v-if="xSurfaceAutomationsPanel" />
    <SurfaceLanguagePanel v-if="xSurfaceLanguagePanel" />
    <SurfacePhoneNumberFieldMethodMenu v-if="xPhoneFieldMethodPopover" />
    <AccessibilitySettingsModal
      v-if="accessibilitySettingsStore.xAccessibilitySettingsModal"
      :visible="accessibilitySettingsStore.xAccessibilitySettingsModal"
      :portal-selector="'#surface-container'"
      :user="user"
      @close="accessibilitySettingsStore.closeAccessibilitySettingsModal"
    />
    <SurfaceScreenRecorder v-if="surfaceScreenRecorderStore.isVisible" />
    <SurfacePhotoAlbumEditorDialog v-if="xPhotoAlbumEditorDialog" />
    <SurfacePhotoAlbumContentPicker v-if="xPhotoAlbumImagePicker" />
    <SurfaceTranslationDrawer v-if="xMobileTranslationBanner" :is-dark-mode="backgroundLuminance === 'dark'" />
    <SurfaceAdditionalOptionsPanel
      v-if="xActionBarMoreMenu"
      @close="
        () => {
          useSurfacePanelsStore().hideActionBarMoreMenu()
        }
      "
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@@/styles/3/modules/all';
.surface-panels {
  .curtain {
    position: fixed;
    top: 0;
    left: 0;
    width: surfaceContainerWidth;
    @include vh100;
    z-index: $sidepanel-zindex - 1;
    background-color: $translucent-backdrop-color;
    -webkit-overflow-scrolling: touch;
    touch-action: manipulation;
    @include respondToTouch {
      cursor: pointer;
    }
  }
}
</style>
